'use client';
import { useState } from 'react';
import WindowIcon from '@mui/icons-material/Window';
import { Box, Button, Typography } from '@mui/material';

import {
  SportsFuturesIcon,
  SportsLiveIcon,
  SportsMobileBottomBar,
  SportsMobileSidebarItem,
  SportsSidebarSkeleton,
} from '@/components';
import {
  checkIsCategoryActive,
  isAutomated,
  isNonAutomated,
  useSportsSidebar,
  useT,
} from '@/hooks';
import { SidebarItem, SportsSidebarCategory } from '@/types';

import { LAYOUT_MAX_WIDTH } from '@/constants';

interface SportsMobileSidebarProps {
  sidebarContext: ReturnType<typeof useSportsSidebar>;
  onSettingsClick?: () => void;
}

/**
 * SportsMobileSidebar handles the sports navigation menu for mobile devices.
 * It features a horizontally scrollable, sticky list of sports categories.
 */
export const SportsMobileSidebar = ({
  sidebarContext,
  onSettingsClick,
}: SportsMobileSidebarProps) => {
  const { state, handlers, refs } = sidebarContext;
  const { allItems, activeId, sportSlug, isLoading } = state;
  const { handleCategoryClick, setCategoryButtonRef } = handlers;
  const { mobileSubCategoryRef } = refs;
  const { t } = useT();

  const [isBottomBarOpen, setIsBottomBarOpen] = useState(false);

  const handleMoreClick = () => {
    setIsBottomBarOpen(!isBottomBarOpen);
  };

  // Render skeleton when data is fetching
  if (isLoading) {
    return <SportsSidebarSkeleton isMobile />;
  }

  // Navigation handlers for top-level filters
  const isLiveActive = sportSlug === 'live';
  const isFuturesActive = sportSlug === 'futures';

  const handleLiveClick = () =>
    handleCategoryClick({
      sport: 'live',
      category: t('sports.live', { defaultValue: 'Live' }),
    } as SidebarItem);
  const handleFuturesClick = () =>
    handleCategoryClick({
      sport: 'futures',
      category: t('sports.futures', { defaultValue: 'Futures' }),
    } as SidebarItem);

  const handleLiveRef = (el: HTMLButtonElement | null) => setCategoryButtonRef('live', el);
  const handleFuturesRef = (el: HTMLButtonElement | null) => setCategoryButtonRef('futures', el);

  const liveVariant = isLiveActive ? 'contained' : 'outlined';
  const futuresVariant = isFuturesActive ? 'contained' : 'outlined';

  /**
   * Maps through all sports categories and generates the navigation items list.
   */
  const renderItemsList = allItems
    .slice(0, 3)
    .map((category: SportsSidebarCategory, index: number) => {
      const isCatActive = checkIsCategoryActive(category, activeId);
      const targetSlug = isNonAutomated(category)
        ? category.leagues?.tag
        : category.sport || category.category?.toLowerCase() || '';

      const handleItemRef = (el: HTMLButtonElement | null) => setCategoryButtonRef(targetSlug, el);

      return (
        <Box key={`${category.category}-${index}`} sx={{ display: 'flex', flexShrink: 0 }}>
          <SportsMobileSidebarItem
            category={category}
            isActive={isCatActive}
            activeId={activeId}
            onClick={handleCategoryClick}
            buttonRef={handleItemRef}
          />
        </Box>
      );
    });

  return (
    <>
      {/* Outer Sticky Container */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          position: 'sticky',
          top: { xs: '56px', sm: '60px', md: '64px' },
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: 'background.default',
          width: '100%',
          mx: { xs: -1, sm: -1.5, md: -2, lg: -2.5 },
          px: { xs: 1, sm: 1.5, md: 2, lg: 2.5 },
          mb: 2,
        }}
      >
        {/* Horizontally Scrollable Content Area */}
        <Box
          ref={mobileSubCategoryRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            width: '100%',
            maxWidth: LAYOUT_MAX_WIDTH,
            mx: 'auto',
            pb: 1,
            pr: 2.5, // Added padding to prevent the last item from being chopped
            overflowX: 'visible',
            overflowY: 'visible',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          <Box sx={{ display: 'flex' }}>
            <Button
              ref={handleLiveRef}
              onClick={handleLiveClick}
              variant={liveVariant}
              disableElevation
              sx={{
                flexShrink: 0,
                borderRadius: 1,
                textTransform: 'none',
                p: 1,
                minWidth: 64,
                height: 64,
                transition: 'all 0.2s',
                bgcolor: isLiveActive ? 'action.selected' : 'transparent',
                border: 'none',
                boxShadow: 'none',
                color: isLiveActive ? 'text.primary' : 'text.secondary',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                '&:hover': {
                  bgcolor: isLiveActive ? 'action.selected' : 'action.hover',
                  boxShadow: 'none',
                },
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SportsLiveIcon />
              </Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1 }}>
                {t('sports.live', { defaultValue: 'Live' })}
              </Typography>
            </Button>
          </Box>

          {/* Dynamic Sports Categories */}
          {renderItemsList}

          {/* More Button */}
          <Box sx={{ display: 'flex' }}>
            <Button
              variant="outlined"
              sx={{
                flexShrink: 0,
                borderRadius: 1,
                textTransform: 'none',
                p: 1,
                minWidth: 64,
                height: 64,
                transition: 'all 0.2s',
                bgcolor: isBottomBarOpen ? 'action.selected' : 'transparent',
                border: 'none',
                color: isBottomBarOpen ? 'text.primary' : 'text.secondary',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                '&:hover': {
                  bgcolor: isBottomBarOpen ? 'action.selected' : 'action.hover',
                },
              }}
              onClick={handleMoreClick}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <WindowIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1 }}>
                {t('nav.more', { defaultValue: 'More' })}
              </Typography>
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Mobile Drawer for ALL SPORTS */}
      <SportsMobileBottomBar
        open={isBottomBarOpen}
        onClose={() => setIsBottomBarOpen(false)}
        onOpen={() => setIsBottomBarOpen(true)}
        sidebarContext={sidebarContext}
      />
    </>
  );
};
